import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useDiscount } from "../lib/discount";
import { useCountry } from "../lib/country";
import { useLanguage } from "../lib/language";
import { useAuth } from "../lib/auth";
import ProductCard from "../components/ProductCard";
import { Sparkles, Truck, ShieldCheck, CreditCard, Tag, BadgeCheck } from "lucide-react";
import IncomingProductsGallery from "../components/IncomingProductsGallery";

export default function Home() {
    const [products, setProducts] = useState([]);
    const [announcement, setAnnouncement] = useState(null);
    const [settings, setSettings] = useState(null);  // null = not yet loaded
    const { discount } = useDiscount();
    const { country } = useCountry();
    const { t } = useLanguage();
    const { user } = useAuth();

    useEffect(() => {
        api.get("/products", { params: { limit: 6, country } }).then(({ data }) => setProducts(data));
        api.get("/announcements").then(({ data }) => setAnnouncement(data[0]));
        api.get("/settings").then(({ data }) => setSettings(data || {}));
    }, [country]);

    // Avoid the "default unsplash → custom banner" flash:
    // While settings are loading we render the hero with NO image (gold/black gradient only).
    // Once settings arrive we use the configured banner if any, otherwise the default fallback.
    const banner = settings === null
        ? null
        : (settings.banner || "https://images.unsplash.com/photo-1779911915389-914430fad5e6?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85");

    return (
        <div className="fade-in">
            {/* HERO */}
            <section className="relative h-[55vh] min-h-[380px] max-h-[560px] overflow-hidden tb-grain bg-[var(--tb-bg-2)]">
                {banner && <img src={banner} alt="" className="absolute inset-0 w-full h-full object-cover fade-in" />}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-black/90" />
                <div className="relative h-full max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-8 items-end pb-10 md:pb-12">
                    <div className="md:col-span-8 z-10 text-white">
                        <div className="tb-overline text-[var(--tb-gold-hi)] mb-3">
                            {country === "US"
                                ? t("Boutique Premium · Estados Unidos", "Premium Fragrance House · United States")
                                : "Boutique Premium · El Salvador"}
                        </div>
                        {country === "US" ? (
                            <>
                                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-4 text-white">
                                    {t("Elegancia embotellada,", "Bottled elegance,")}
                                    <br />
                                    <span className="italic text-[var(--tb-gold-hi)]">
                                        {t("destilada a la perfección.", "distilled to perfection.")}
                                    </span>
                                </h1>
                                <p className="text-white/80 max-w-xl text-sm sm:text-base leading-relaxed mb-5">
                                    {t(
                                        "Fragancias seleccionadas y esencias que dicen más que las palabras. Envíos USPS a los 50 estados, pagos en USD con Stripe o PayPal.",
                                        "Curated perfumes and scents that speak louder than words. USPS shipping across all 50 states, USD checkout via Stripe or PayPal."
                                    )}
                                </p>
                            </>
                        ) : (
                            <>
                                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-4 text-white">
                                    Lujo curado<br /><span className="italic text-[var(--tb-gold-hi)]">para el alma</span> latina.
                                </h1>
                                <p className="text-white/80 max-w-xl text-sm sm:text-base leading-relaxed mb-5">
                                    Bolsos, joyería y moda seleccionada con un estándar artesanal. Pagos en USD con Stripe, PayPal, transferencia o apartado en tienda.
                                </p>
                            </>
                        )}
                        <div className="flex gap-3 flex-wrap">
                            <Link to="/tienda" className="tb-btn-primary" data-testid="hero-shop-cta">
                                {t("Explorar colección", "Explore Collection")}
                            </Link>
                            <Link to="/como-usar" className="tb-btn-secondary !text-white !border-white hover:!bg-white/10" data-testid="hero-howto-cta">
                                {t("Cómo usar", "How to buy")}
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ANNOUNCEMENT */}
            {announcement && (
                <section className="bg-[var(--tb-gold)] text-white py-3 px-6 text-center text-sm font-medium tracking-wide" data-testid="announcement-banner">
                    {announcement.body}
                </section>
            )}

            {/* DISCOUNT BANNER */}
            {discount && discount.active && (
                <section className="relative overflow-hidden" data-testid="discount-banner">
                    <div className="max-w-7xl mx-auto px-6 py-8">
                        <div className="relative grid md:grid-cols-2 gap-6 items-center bg-gradient-to-r from-[var(--tb-bg-2)] to-transparent border border-[var(--tb-gold)]/40 p-6 md:p-10 overflow-hidden">
                            <div className="space-y-3 z-10">
                                <div className="tb-overline text-[var(--tb-gold)] flex items-center gap-2"><Tag className="w-4 h-4" /> Oferta activa</div>
                                <h2 className="font-display text-3xl sm:text-4xl">{discount.banner_title || discount.name}</h2>
                                {discount.banner_subtitle && <p className="text-[var(--tb-text-2)] text-sm">{discount.banner_subtitle}</p>}
                                <div className="inline-flex items-baseline gap-2 bg-[var(--tb-gold)]/15 px-4 py-2 border border-[var(--tb-gold)]/40">
                                    <span className="font-display text-3xl text-[var(--tb-gold)]">
                                        {discount.type === "percent" ? `${discount.value}%` : `$${discount.value}`}
                                    </span>
                                    <span className="text-xs text-[var(--tb-text-2)] uppercase tracking-wider">{t("de descuento", "off")}</span>
                                </div>
                                <div className="text-xs text-[var(--tb-muted)]">
                                    {discount.scope === "all" ? t("Aplica a todo el inventario", "Applies to the entire catalog") : `${t("Aplica a", "Applies to")}: ${(discount.categories || []).join(", ")}`}
                                </div>
                                <Link to="/tienda" className="tb-btn-primary inline-block !text-sm mt-2">{t("Comprar ahora", "Shop now")}</Link>
                            </div>
                            {discount.banner_image && (
                                <div className="hidden md:block relative aspect-square max-h-64 mx-auto md:mx-0">
                                    <img src={discount.banner_image} alt="" className="w-full h-full object-cover rounded" />
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {/* USP */}
            <section className={`max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-20 grid gap-3 md:gap-6 stagger grid-cols-2 ${country === "SV" ? "md:grid-cols-2 lg:grid-cols-5" : "md:grid-cols-4"}`}>
                {[
                    { icon: Sparkles, title: t("Curaduría Premium", "Premium Curation"), desc: t("Cada pieza, seleccionada a mano.", "Every scent, hand-picked.") },
                    { icon: Truck, title: country === "US" ? t("Envíos a todo Estados Unidos", "US-wide shipping") : "Envíos en El Salvador", desc: country === "US" ? t("USPS Ground a los 50 estados.", "USPS Ground to all 50 states.") : "Recibe o recoge en tienda." },
                    { icon: CreditCard, title: t("Pagos Flexibles", "Flexible Checkout"), desc: country === "US" ? t("Tarjeta o PayPal.", "Card or PayPal.") : "Tarjeta, PayPal, transferencia o apartado." },
                    { icon: ShieldCheck, title: t("Compras Verificadas", "Verified Purchases"), desc: t("Verificación de identidad y soporte 24/7.", "Identity verification and 24/7 support.") },
                ].map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="tb-card p-3 md:p-6 flex md:block items-start gap-3">
                        <Icon className="w-5 h-5 md:w-6 md:h-6 text-[var(--tb-gold)] flex-shrink-0 md:mb-4 mt-0.5 md:mt-0" />
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-sm md:text-lg leading-tight md:mb-1">{title}</div>
                            <p className="text-[11px] md:text-sm text-[var(--tb-text-2)] leading-snug mt-0.5 md:mt-0">{desc}</p>
                        </div>
                    </div>
                ))}
                {country === "SV" && (
                    <div className="tb-card p-3 md:p-6 border-[var(--tb-border-gold)]/40 bg-[var(--tb-border-gold)]/5 flex md:flex-col col-span-2 lg:col-span-1 items-start md:items-stretch gap-3 md:gap-0" data-testid="verified-benefits-card">
                        <BadgeCheck className="w-5 h-5 md:w-6 md:h-6 text-[#2196F3] fill-[#2196F3]/20 flex-shrink-0 md:mb-4 mt-0.5 md:mt-0" />
                        <div className="min-w-0 flex-1 md:flex md:flex-col md:flex-1">
                            <div className="font-display text-sm md:text-lg leading-tight md:mb-1">Beneficios al Verificarte</div>
                            <ul className="text-[10px] md:text-xs text-[var(--tb-text-2)] space-y-0.5 md:space-y-1.5 mt-1 md:mt-0 mb-2 md:mb-4 md:flex-1">
                                <li>✓ Check azul en tu perfil</li>
                                <li>✓ Apartado en tienda (efectivo)</li>
                                <li>✓ Pago contra entrega (CCE)</li>
                                <li>✓ Soporte prioritario</li>
                            </ul>
                            <Link to={user ? "/perfil?tab=verificacion" : "/register"}
                                className="tb-btn-primary !text-[10px] md:!text-xs w-full text-center inline-flex items-center justify-center gap-1 !py-1.5 md:!py-2"
                                data-testid="verified-benefits-cta">
                                <BadgeCheck className="w-3 h-3 md:w-3.5 md:h-3.5" />
                                {user ? (user.verified ? "Ya estás verificado" : "Verificarme") : "Crear cuenta y verificarme"}
                            </Link>
                        </div>
                    </div>
                )}
            </section>

            {/* PRODUCTS */}
            <section className="max-w-7xl mx-auto px-6 pb-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="tb-overline text-[var(--tb-gold)] mb-3">{t("Colección destacada", "Featured Collection")}</div>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight">{t("Piezas selectas de la temporada", "Selected pieces of the season")}</h2>
                    </div>
                    <Link to="/tienda" className="tb-link text-sm hidden md:inline">{t("Ver todo →", "View all →")}</Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 stagger">
                    {products.map((p) => <ProductCard key={p.sku} p={p} />)}
                </div>
            </section>

            {/* INCOMING PRODUCTS */}
            {country === "SV" && <IncomingProductsGallery />}
        </div>
    );
}
